import axios from "axios";
import i18n from "../i18n";
import { API_BASE_URL } from "../utils/config";

import type { FlowMode } from "../types";

// ============================================================================
// 타입 정의 (DTO)
// 서버 응답 구조를 TS 인터페이스로 정의하여 자동완성과 타입 체크를 지원합니다.
// ============================================================================

// 공통 응답 래퍼 (Spring Boot의 표준 응답 구조에 맞춤)
export interface BaseResponse<T = unknown> {
  resultCode: number;
  resultMsg: string;
  resultData: T;
}

// /api/score 응답 타입
export interface ScoreData {
  flowMode: FlowMode;
  suid: string; // 사용자 Sequence ID
  briefingDone: boolean; // 브리핑 음원을 끝까지 청취한 적이 있는지
  score: {
    lbi: string; // 좌뇌 (1번 축)
    rbi: string; // 우뇌 (2번 축)
    pei: string; // 신체활동성 (3번 축)
    ai: string; // 진취성 (4번 축)
    hi: string; // 사회성 (5번 축)
    wci: string; // 실행성 (6번 축)
    math: string; // [제외 대상] 수학 점수?
  };
}

// /api/consulting 응답 타입
export interface BriefData {
  audio_url: string;
  status: string;
  version: string;
}

// /api/tts 응답 타입
export interface TTSData {
  audio_url: string;
  message: string;
  file_id: string;
}

// /answer 응답 타입 (AI 답변)
export interface AnswerData {
  audioUrl: string; // 답변 TTS 오디오 URL
  answerText?: string; // (옵션) 텍스트 답변이 있다면
}

// WS Ticket 응답 타입
export interface WsTicketData {
  ticket: string;
  totalQuestionQuota: number;
  usedQuestionCount: number;
}

interface ConsultingErrorResponse {
  status?: boolean;
  error?: string;
}

// ============================================================================
// Axios 인스턴스 설정
// 인증(Cookie)과 기본 설정을 중앙에서 관리합니다.
// ============================================================================

const apiClient = axios.create({
  // 개발 모드일 때는 Proxy를 타도록 빈 문자열로 설정
  baseURL: API_BASE_URL,
  timeout: 30000, // AI 응답이 느릴 수 있으므로 30초로 넉넉하게 설정
  withCredentials: true, // JSESSIONID 쿠키를 포함시켜 로그인 상태 유지
  headers: {
    "Content-Type": "application/json",
  },
});

// 요청 인터셉터 (로그인 체크 등 필요시 확장)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // 세션 만료 시 소개 페이지로 리다이렉트 (window.location 사용)
      alert(i18n.t("sessionExpired"));

      // 세션 정보 삭제 (보안)
      sessionStorage.removeItem("return_url");

      // 기존 시스템(Spring Boot)으로 복귀
      window.location.replace(API_BASE_URL);
    }

    if (error.response && error.response.status === 402) {
      // 결제 미완료 시 메인 페이지(요금제 선택)로 이동
      window.location.replace(API_BASE_URL || "/");
    }

    return Promise.reject(error);
  },
);

// ============================================================================
// API 함수 모음
// ============================================================================

/**
 * [GET] /api/score
 *
 * 초기 상담 데이터(점수)를 가져옵니다.
 *
 * @param {string | null} requestedMode 요청 모드
 * @returns {Promise<ScoreData>} 초기 상담 데이터
 */
export const getScoreData = async (
  requestedMode?: string | null,
): Promise<ScoreData> => {
  const { data } = await apiClient.get<BaseResponse<ScoreData>>("/api/score", {
    params: { mode: requestedMode },
  });

  if (data.resultMsg !== "success") {
    throw new Error(data.resultMsg || i18n.t("dataLoadFailed"));
  }
  return data.resultData;
};

/**
 * [POST] /api/consulting
 *
 * 뇌인지 브리핑 오디오 데이터를 가져옵니다.
 *
 * @param {ScoreData} scoreData 지수 데이터
 * @param {string} lang 화면 진입 시 전달받은 언어 값
 * @returns {BriefData} 뇌인지 브리핑 데이터
 */
export const getBriefData = async (
  scoreData: ScoreData,
  lang: string,
): Promise<BriefData> => {
  try {
    const formData = new FormData();
    formData.append("id", scoreData.suid);
    formData.append("lbi", scoreData.score.lbi);
    formData.append("rbi", scoreData.score.rbi);
    formData.append("pei", scoreData.score.pei);
    formData.append("ai", scoreData.score.ai);
    formData.append("hi", scoreData.score.hi);
    formData.append("wci", scoreData.score.wci);
    formData.append("math", scoreData.score.math);
    formData.append("lang", lang);

    const { data } = await apiClient.post<BaseResponse<BriefData>>(
      "/api/consulting",
      formData,
    );

    if (data.resultCode !== 0) {
      throw new Error(data.resultMsg || i18n.t("briefingLoadFailed"));
    }

    return data.resultData;
  } catch (error) {
    if (axios.isAxiosError<ConsultingErrorResponse>(error)) {
      const serverError = error.response?.data?.error;
      if (typeof serverError === "string" && serverError.trim()) {
        throw new Error(serverError);
      }
    }

    throw error;
  }
};

/**
 * [POST] /api/consulting/start
 *
 * 상담 시작 시간 저장.
 */
export const startConsulting = () => {
  apiClient.post<BaseResponse>("/api/consulting/start");
};

/**
 * [POST] /api/consulting/status
 *
 * 상담 완료 상태를 서버에 저장합니다.
 */
export const completeConsultation = () => {
  apiClient.post<BaseResponse>("/api/consulting/status");
};

/**
 * [POST] /api/consulting/briefing-complete
 *
 * 브리핑 음원을 끝까지 청취한 시점을 서버에 저장합니다.
 * 이후 재진입 시 브리핑을 건너뛰는 데 사용됩니다.
 */
export const completeBriefing = () => {
  apiClient.post<BaseResponse>("/api/consulting/briefing-complete");
};

/**
 * [POST] /api/ws-ticket
 *
 * WebSocket 연결을 위한 티켓을 발급받습니다.
 *
 * @returns {WsTicketData}
 */
export const fetchWsTicket = async (): Promise<WsTicketData> => {
  const { data } = await apiClient.post<WsTicketData>("/api/ws-ticket");
  return data;
};
